import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { useTranslation } from "react-i18next";
import SpeakersGrid from "../../components/2Speakers/SpeakersGrid";
import BookingSection from "../../pages/Home/BookingSection";
import AOS from "aos";
import "aos/dist/aos.css";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../AuthContext";
import {
  getFollowers,
  getFollowing,
  followUser,
  unfollowUser,
} from "../../Api/followService";
import SpeakerFilters from "../../pages/Home/SpeakerFilters";

const Speakers = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const [speakers, setSpeakers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    keyword: "",
    countryIds: [],
    cityIds: [],
    industryIds: [],
    specialityIds: [],
    gender: "",
    language: "",
    price: "",
  });

  const navigate = useNavigate();
  const page = parseInt(searchParams.get("page")) || 1;
  const [hasFiltered, setHasFiltered] = useState(false);
  const isArabic = i18n.language === "ar";
  const [following, setFollowing] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const { user, isAuthenticated } = useAuth();
  const [priceRanges, setPriceRanges] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const currentLang = i18n.language;
  const [hasSearched, setHasSearched] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    specialities: 0,
    countries: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const toggleFavorite = async (speakerId) => {
    if (isAuthenticated) {
      try {
        const isFavorite = favorites.includes(speakerId);
        const action = isFavorite ? "remove" : "add";

        if (action === "add") {
          await axiosInstance.post(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteAdded"));
        } else {
          await axiosInstance.delete(`/api/v1/favorites/speakers/${speakerId}`);
          toast.success(t("favoriteRemoved"));
        }

        // تحديث قائمة المفضلات بعد الإضافة أو الحذف
        setFavorites((prevFavorites) => {
          if (isFavorite) {
            return prevFavorites.filter((id) => id !== speakerId);
          }
          return [...prevFavorites, speakerId];
        });
      } catch (error) {
        console.error("Error updating favorite:", error);
        toast.error(t("errorUpdatingFavorite"));
      }
    } else {
      toast.warn(t("notAuthenticated"));
    }
  };

  const toggleFollow = async (speakerId) => {
    if (isAuthenticated) {
      const isCurrentlyFollowing = following.includes(speakerId);
      try {
        if (isCurrentlyFollowing) {
          await unfollowUser(speakerId);
          toast.success(t("unfollowSuccess"));
          setFollowing((prev) => prev.filter((id) => id !== speakerId));
        } else {
          await followUser(speakerId);
          toast.success(t("followSuccess"));
          setFollowing((prev) => [...prev, speakerId]);
        }
      } catch (error) {
        console.error("Error toggling follow:", error);
        toast.error(t("errorUpdatingFollow"));
      }
    } else {
      toast.warn(t("notAuthenticated"));
    }
  };

  // الفلاتر

  const getLabel = (item) =>
    currentLang === "ar" ? item.arabicName : item.englishName;

  // دالعة عرض نتائج البحث
  const renderSearchSummary = () => {
    const parts = [];

    if (filters.keyword) parts.push(`${t("name")}: ${filters.keyword}`);

    if (filters.countryIds.length > 0) {
      const country = countries.find((c) => c.id === filters.countryIds[0]);

      if (country) parts.push(`${t("country")}: ${getLabel(country)}`);
    }

    if (filters.cityIds.length > 0) {
      const city = cities.find((c) => c.id === filters.cityIds[0]);

      if (city) parts.push(`${t("city")}: ${getLabel(city)}`);
    }

    if (filters.industryIds.length > 0) {
      const industry = industries.find((c) => c.id === filters.industryIds[0]);

      if (industry) parts.push(`${t("industry")}: ${getLabel(industry)}`);
    }

    if (filters.specialityIds.length > 0) {
      const speciality = specialities.find(
        (c) => c.id === filters.specialityIds[0]
      );

      if (speciality) parts.push(`${t("speciality")}: ${getLabel(speciality)}`);
    }

    return parts.length > 0
      ? `${t("search_results_for")}: ${parts.join("، ")}`
      : null;
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFilters((prev) => ({ ...prev, [id]: value }));
  };

 const resetFilters = () => {
  const initialFilters = {
    keyword: "",
    countryIds: [],
    cityIds: [],
    industryIds: [],
    specialityIds: [],
    language: "",
    gender: "",
    priceRangeId: "",
  };

  setFilters(initialFilters);
  setCurrentPage(1);
  setHasSearched(false);
  setSearchParams(new URLSearchParams());

  handleFilterChange(initialFilters); // لتحديث URL ونتائج البحث
  fetchSpeakers(); // تحميل النتائج الجديدة
  window.scrollTo(0, 0);
};


  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    setHasSearched(true);

    const params = new URLSearchParams();

    if (newFilters.keyword) params.set("name", newFilters.keyword);

    newFilters.countryIds.forEach((id) => params.append("countryIds", id));
    newFilters.cityIds.forEach((id) => params.append("cityIds", id));
    newFilters.industryIds.forEach((id) => params.append("industryIds", id));
    newFilters.specialityIds.forEach((id) =>
      params.append("specialitiesId", id)
    );

    if (newFilters.gender) params.set("gender", newFilters.gender);
    if (newFilters.priceRangeId) params.set("price", newFilters.priceRangeId);


    params.set("page", 1);

    setSearchParams(params);
  };

  const onFilterChange = (newFilters) => {
    console.log("New Filters: ", newFilters);
    handleFilterChange(newFilters);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage);
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
  setCurrentPage(1);
  setHasSearched(true);
  fetchSpeakers();
  window.scrollTo(0, 0); 
};


  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/v1/users/speakers", {
        params: {
          Keyword: filters.keyword || "",
          CountryIds: filters.countryIds.length
            ? filters.countryIds
            : undefined,
          CityIds: filters.cityIds.length ? filters.cityIds : undefined,
          IndustryIds: filters.industryIds.length
            ? filters.industryIds
            : undefined,
          SpecialitiesId: filters.specialityIds.length
            ? filters.specialityIds
            : undefined,
          Gender: filters.gender || undefined,
          Language: filters.language || undefined,
          Price: filters.priceRangeId || undefined, 
          Page: currentPage,
          PageCount: 12,
        },
      });

      const items = response.data.items || [];
      setSpeakers(items);
      setTotalPages(response.data.metadata?.totalPages || 1);

      // 🧮 حساب الإحصائيات مثل القديمة
      const uniqueSpecialities = new Set();
      const uniqueCountries = new Set();

      items.forEach((speaker) => {
        speaker.speakerSpecialies?.forEach((s) =>
          uniqueSpecialities.add(s.specialityId)
        );

        if (speaker.nationality?.id) {
          uniqueCountries.add(speaker.nationality.id);
        }
      });

      setStats({
        total: response.data.metadata?.totalCount || items.length,
        specialities: uniqueSpecialities.size,
        countries: uniqueCountries.size,
      });
    } catch (error) {
      console.error("Error fetching speakers:", error);
      toast.error(error?.detail || error?.title || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (countryId) => {
    if (!countryId) {
      setCities([]);
      return;
    }

    try {
      const citiesResponse = await axiosInstance.get(
        `/api/v1/lookups/cities?countryId=${countryId}`
      );
      setCities(citiesResponse.data.items);
    } catch (error) {
      setCities([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, specialitiesRes, industriesRes, pricesRes] =
          await Promise.all([
            axiosInstance.get("/api/v1/lookups/countries"),
            axiosInstance.get("/api/v1/lookups/specialities"),
            axiosInstance.get("/api/v1/lookups/industries"),
            axiosInstance.get("/api/v1/lookups/prices-range"),
          ]);

        setCountries(countriesRes.data.items);
        setSpecialities(specialitiesRes.data.items);
        setIndustries(industriesRes.data.items);
        setPriceRanges(pricesRes.data.items);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchData();
  }, []);

  // جلب المتحدثين
  useEffect(() => {
    fetchSpeakers();
  }, [currentPage]);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const fetchFavorites = async () => {
        try {
          const response = await axiosInstance.get(
            `/api/v1/favorites/speakers/${user.id}`
          );
          setFavorites(response.data.favorites || []);
        } catch (error) {
          console.error("Error fetching favorites:", error);
        }
      };

      fetchFavorites();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page")) || 1;
    const newFilters = {
      keyword: searchParams.get("name") || "",
      countryIds: searchParams.getAll("countryIds"),
      cityIds: searchParams.getAll("cityIds"),
      industryIds: searchParams.getAll("industryIds"),
      specialityIds: searchParams.getAll("specialitiesId"),
      gender: searchParams.get("gender") || "",
      price: searchParams.get("price") || "",
    };

    setCurrentPage(pageParam);
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  if (loading) return <div className="text-center py-5">جارٍ التحميل...</div>;
  if (error) return <div className="text-danger text-center py-5">{error}</div>;

  return (
    <div dir={languageDirection}>
      <section
        className="bg-light position-relative overflow-hidden"
        style={{ "--bs-bg-opacity": 0.8 }}
      >
        {/* Decorative Patterns */}
        <div
          className="position-absolute top-0 start-0 d-none d-lg-block"
          style={{ opacity: 0.07 }}
        >
          <svg width="180" height="180" viewBox="0 0 200 200">
            <path
              d="M45,0 L0,45 L45,90 L90,45 Z"
              fill="#6B72E0"
              transform="translate(0, 0)"
            ></path>
            <path
              d="M45,0 L0,45 L45,90 L90,45 Z"
              fill="#6B72E0"
              transform="translate(80, 80)"
            ></path>
          </svg>
        </div>
        <div
          className="position-absolute bottom-0 end-0 d-none d-lg-block"
          style={{ opacity: 0.07 }}
        >
          <svg width="150" height="150" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="50" fill="#6B72E0"></circle>
            <circle cx="50" cy="50" r="35" fill="#ffffff"></circle>
            <circle cx="50" cy="50" r="20" fill="#6B72E0"></circle>
          </svg>
        </div>

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <div
                className="d-inline-flex justify-content-center align-items-center bg-primary-custom rounded-circle mb-3"
                style={{ width: "60px", height: "60px" }}
              >
                <i className="fas fa-microphone-alt fa-lg text-white"></i>
              </div>
              <h1
                className="fw-bold text-primary-custom mb-3"
                data-aos="fade-up"
              >
                {t("Speaker")}
              </h1>

              <p className="lead mb-4" data-aos="fade-up" data-aos-delay="200">
                {t("Explorelist")}
              </p>

              <div className="row g-4 justify-content-center mt-2">
                <div
                  className="col-md-4 col-sm-4"
                  data-aos="zoom-in"
                  data-aos-delay="100"
                >
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body text-center py-4">
                      <h2 className="fw-bold text-primary-custom mb-0">
                        +{stats.total}
                      </h2>
                      <p className="text-muted mb-0">
                        {t("ProfessionalSpeaker")}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="col-md-4 col-sm-4"
                  data-aos="zoom-in"
                  data-aos-delay="200"
                >
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body text-center py-4">
                      <h2 className="fw-bold text-primary-custom mb-0">
                        {stats.specialities}
                      </h2>
                      <p className="text-muted mb-0">
                        {t("FieldSpecialization")}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="col-md-4 col-sm-4"
                  data-aos="zoom-in"
                  data-aos-delay="300"
                >
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body text-center py-4">
                      <h2 className="fw-bold text-primary-custom mb-0">
                        {stats.countries}
                      </h2>
                      <p className="text-muted mb-0">
                        {t("DifferentCountries")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div id="speakers-section" className="py-5">
        <div className="container">
          <div className="card shadow-sm mb-5">
            <div className="card-body p-4">
              {/* الفلترة  */}
              <div className="row g-3" dir={languageDirection}>
                <SpeakerFilters
                  filters={filters}
                  countries={countries}
                  cities={cities}
                  industries={industries}
                  specialities={specialities}
                  priceRanges={priceRanges}
                  handleInputChange={handleInputChange}
                  resetFilters={resetFilters}
                  handleSearch={handleSearch}
                  fetchCities={fetchCities}
                  onFilterChange={onFilterChange}
                />
              </div>
            </div>
          </div>

          {/* بطاقات المتحدثين */}
          {speakers.length > 0 ? (
            <>
              {hasSearched && renderSearchSummary() && (
                <div className="alert alert-info mt-4" data-aos="fade-in">
                  {renderSearchSummary()}
                </div>
              )}

              <div className="row" id="speakersSearchResults">
                <div className="row g-4 mb-5">
                  {speakers.map((speaker) => (
                    <div
                      className="col-xl-3 col-lg-4 col-md-6"
                      data-aos="zoom-in"
                      key={speaker.id}
                    >
                      <a
                        href={`/speaker-detail/${speaker.id}`}
                        className="speaker-card-link"
                      >
                        <div className="card speaker-card shadow-sm h-100 position-relative">
                          <div className="speaker-img-container position-relative">
                            {isAuthenticated && (
                              <>
                                {/* زر المفضلة */}
                                <button
                                  className={`favorite-btn position-absolute top-0 end-0 m-2 border-0 rounded-circle ${
                                    favorites.includes(speaker.id)
                                      ? "text-warning"
                                      : "text-secondary"
                                  } bg-white shadow`}
                                  aria-label="إضافة إلى المفضلة"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleFavorite(speaker.id);
                                  }}
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    zIndex: 10,
                                  }}
                                >
                                  <i className="fas fa-star"></i>
                                </button>
                                {/* زر المتابعة */}
                                <button
                                  className={`follow-btn position-absolute top-0 start-0 m-2 border-0 rounded-circle ${
                                    following.includes(speaker.id)
                                      ? "text-success"
                                      : "text-secondary"
                                  } bg-white shadow`}
                                  aria-label="متابعة"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toggleFollow(speaker.id);
                                  }}
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    zIndex: 10,
                                  }}
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>
                              </>
                            )}

                            <img
                              src={speaker.profileImage}
                              className="speaker-img"
                              alt={
                                isArabic
                                  ? speaker.arabicFullName
                                  : speaker.englishFullName
                              }
                            />
                            <div className="speaker-bio-overlay">
                              <div className="speaker-bio-content">
                                <p>
                                  {isArabic
                                    ? speaker.arabicShortBiography
                                    : speaker.englishShortBiography}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="card-body">
                            <h5 className="card-title fw-bold">
                              {isArabic
                                ? speaker.arabicFullName
                                : speaker.englishFullName}
                            </h5>
                            {/* <p className="card-text text-muted">
                                   {isArabic ? speaker.arabicJobTitle : speaker.englishJobTitle}
                                 </p> */}
                            <div className="speaker-location">
                              <i className="fas fa-map-marker-alt me-1"></i>
                              <span>
                                {isArabic
                                  ? speaker.city?.arabicName
                                  : speaker.city?.englishName}
                                ,{" "}
                                {isArabic
                                  ? speaker.city?.state?.arabicName
                                  : speaker.city?.state?.englishName}
                              </span>
                            </div>
                            <div className="mt-2">
                              {speaker.speakerSpecialies?.map(
                                (speciality, index) => (
                                  <span key={index} className="topic-badge">
                                    {isArabic
                                      ? speciality.speciality.arabicName
                                      : speciality.speciality.englishName}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="text-muted">{t("no_results_found")}</p>
          )}

          {/* Pagination Controls */}
          <nav aria-label="Page navigation">
            <ul className="pagination justify-content-center custom-pagination">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => {
                    if (currentPage > 1) {
                      handlePageChange(currentPage - 1);
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 0);
                    }
                  }}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
              </li>

              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => {
                        handlePageChange(pageNum);
                        setTimeout(() => {
                          window.scrollTo(0, 0);
                        }, 0);
                      }}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      handlePageChange(currentPage + 1);
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 0);
                    }
                  }}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <BookingSection />
      <ToastContainer />
    </div>
  );
};

export default Speakers;
